// ─── What: the position panel ─────────────────────────────────────────────────
//
// Only exists when a trader is highlighted. Two series share the panel: the step
// line is share count on the left scale, the dashed line is PnL on the right.

import * as d3 from 'd3';
import { drawPanelSeparator } from '../../chartAxis';
import { placeTooltip, buildPositionTooltipHtml } from '../../chartTooltips';
import { nearestOhlcv } from '../../chartData';
import type { PositionSnap, TradeOverlayData } from '../../chartTypes';
import { MARGIN } from '../layout';
import { drawPanelToggle } from './panelChrome';
import {
    buildPositionSnapshots,
    buildPnlPoints,
    extentIncludingZero,
} from './positionMath';
import type { ChartChrome, DrawCtx } from '../drawContext';

export interface PositionPanelDeps {
    tradeOverlay: TradeOverlayData;
    chrome: ChartChrome;
    onToggle: () => void;
}

/** Compact signed money, e.g. +$1.2K / -$340. */
function fmtSignedUsd(d: number): string {
    const abs = Math.abs(d);
    const sign = d < 0 ? '-' : d > 0 ? '+' : '';
    if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(1)}M`;
    if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
    return `${sign}$${abs.toFixed(0)}`;
}

/** Compact share count. */
function fmtSharesTick(d: number): string {
    const abs = Math.abs(d);
    return abs >= 1e6
        ? `${(d / 1e6).toFixed(1)}M`
        : abs >= 1e3
          ? `${(d / 1e3).toFixed(1)}K`
          : d.toFixed(1);
}

export function drawPositionPanel(ctx: DrawCtx, deps: PositionPanelDeps): void {
    const {
        svg,
        defs,
        layout,
        colors,
        xScale,
        series,
        posTooltipRef,
        svgEl,
    } = ctx;
    const { tradeOverlay, chrome } = deps;
    const { w, posTop, posBottom, posHeight, posVisible } = layout;
    const { isDark, gridColor, textColor, tc } = colors;

    drawPanelSeparator(svg, MARGIN.left, MARGIN.left + w, posTop - 1, gridColor);

    drawPanelToggle(svg, {
        top: posTop,
        centreX: MARGIN.left + w / 2,
        name: 'POSITION',
        open: posVisible,
        textColor,
        onToggle: deps.onToggle,
    });

    if (!posVisible) return;

    defs.append('clipPath')
        .attr('id', layout.clipIds.pos)
        .append('rect')
        .attr('x', MARGIN.left)
        .attr('y', posTop)
        .attr('width', w)
        .attr('height', posHeight);

    const positionsByAccount = buildPositionSnapshots(
        tradeOverlay.trades,
        tradeOverlay.highlightedAccounts,
        series[0]?.data ?? [],
        xScale.domain()[1] as Date
    );
    if (positionsByAccount.size === 0) return;

    const pnlPointsByAccount = buildPnlPoints(
        positionsByAccount,
        series[0]?.data ?? []
    );

    const [minPos, maxPos] = extentIncludingZero(positionsByAccount, p => p.pos);
    const [minPnl, maxPnl] = extentIncludingZero(
        pnlPointsByAccount,
        p => p.totalPnl
    );

    const posPad = Math.max(0.1, (maxPos - minPos) * 0.1);
    const posScale = d3
        .scaleLinear()
        .domain([minPos - posPad, maxPos + posPad])
        .range([posBottom - 4, posTop + 14]);

    const pnlPad = Math.max(0.01, (maxPnl - minPnl) * 0.12);
    const pnlScale = d3
        .scaleLinear()
        .domain([minPnl - pnlPad, maxPnl + pnlPad])
        .range([posBottom - 4, posTop + 14]);

    // PnL ticks, inset from the share ticks so the two right-hand scales read apart.
    svg.append('g')
        .selectAll('text.pnl-tick')
        .data(pnlScale.ticks(3))
        .join('text')
        .attr('x', MARGIN.left + w + 8)
        .attr('y', d => pnlScale(d) + 3)
        .attr('fill', textColor)
        .attr('font-size', '8px')
        .attr('font-family', 'monospace')
        .attr('text-anchor', 'end')
        .attr('opacity', 0.6)
        .text(fmtSignedUsd);

    // Zero line only when the position actually crosses it.
    if (minPos < 0 && maxPos > 0) {
        svg.append('line')
            .attr('x1', MARGIN.left)
            .attr('x2', MARGIN.left + w)
            .attr('y1', posScale(0))
            .attr('y2', posScale(0))
            .attr('stroke', textColor)
            .attr('stroke-width', 0.5)
            .attr('stroke-dasharray', '3 3');
    }

    const posTicks = posScale.ticks(3);
    svg.append('g')
        .selectAll('line')
        .data(posTicks)
        .join('line')
        .attr('x1', MARGIN.left)
        .attr('y1', d => posScale(d))
        .attr('x2', MARGIN.left + w)
        .attr('y2', d => posScale(d))
        .attr('stroke', gridColor)
        .attr('stroke-width', 0.5);
    svg.append('g')
        .selectAll('text')
        .data(posTicks)
        .join('text')
        .attr('x', MARGIN.left + w + 4)
        .attr('y', d => posScale(d) + 3)
        .attr('fill', textColor)
        .attr('font-size', '8px')
        .attr('font-family', 'monospace')
        .attr('text-anchor', 'start')
        .text(fmtSharesTick);

    const posLineGen = d3
        .line<PositionSnap>()
        .x(d => xScale(d.t))
        .y(d => posScale(d.pos))
        .curve(d3.curveStepAfter);

    const posGroup = svg
        .append('g')
        .attr('clip-path', `url(#${layout.clipIds.pos})`);

    const zeroY = pnlScale(0);
    const pnlLineGen = d3
        .line<PositionSnap>()
        .x(d => xScale(d.t))
        .y(d => pnlScale(d.totalPnl))
        .curve(d3.curveMonotoneX);
    const pnlAreaGen = d3
        .area<PositionSnap>()
        .x(d => xScale(d.t))
        .y0(zeroY)
        .y1(d => pnlScale(d.totalPnl))
        .curve(d3.curveMonotoneX);

    for (const [account, points] of positionsByAccount) {
        const acctColor = tradeOverlay.accountColorMap.get(account) ?? '#888';
        const pnlPoints = pnlPointsByAccount.get(account) ?? [];

        posGroup
            .append('path')
            .datum(points)
            .attr('d', posLineGen)
            .attr('fill', 'none')
            .attr('stroke', acctColor)
            .attr('stroke-width', 1.5)
            .attr('opacity', 0.85);

        const last = points[points.length - 1];
        if (last) {
            posGroup
                .append('circle')
                .attr('cx', xScale(last.t))
                .attr('cy', posScale(last.pos))
                .attr('r', 3)
                .attr('fill', acctColor)
                .attr('stroke', isDark ? '#000' : '#fff')
                .attr('stroke-width', 1);
        }

        if (pnlPoints.length > 1) {
            // Both fills use the same generator and clip to the zero baseline by
            // overlap; only the colours differ.
            posGroup
                .append('path')
                .datum(pnlPoints)
                .attr('d', pnlAreaGen)
                .attr('fill', acctColor)
                .attr('opacity', 0.1);
            posGroup
                .append('path')
                .datum(pnlPoints)
                .attr('d', pnlAreaGen)
                .attr('fill', isDark ? '#f87171' : '#dc2626')
                .attr('opacity', 0.1);

            posGroup
                .append('path')
                .datum(pnlPoints)
                .attr('d', pnlLineGen)
                .attr('fill', 'none')
                .attr('stroke', acctColor)
                .attr('stroke-width', 1.5)
                .attr('stroke-dasharray', '3,2')
                .attr('opacity', 0.7);
        }
    }

    // Its own tooltip element, so it can sit open alongside the price tooltip.
    svg.append('rect')
        .attr('x', MARGIN.left)
        .attr('y', posTop)
        .attr('width', w)
        .attr('height', posHeight)
        .attr('fill', 'transparent')
        .style('cursor', 'crosshair')
        .on('mousemove', (event: MouseEvent) => {
            const [mx] = d3.pointer(event, svgEl);
            chrome.showCross(mx);

            const hoverDate = xScale.invert(mx);
            let currentProb = 0.5;
            const probSeries = series[0];
            if (probSeries && probSeries.data.length > 0) {
                const pt = nearestOhlcv(probSeries.data, hoverDate);
                if (pt) currentProb = pt.close;
            }

            placeTooltip(
                posTooltipRef.current,
                event,
                buildPositionTooltipHtml(
                    positionsByAccount,
                    hoverDate,
                    currentProb,
                    tradeOverlay.accountColorMap,
                    tc
                )
            );
        })
        .on('mouseleave', () => {
            chrome.hideCross();
            d3.select(posTooltipRef.current).style('display', 'none');
        });
}
